import angular from 'angular';

import locationBlueSvg from '../../../assets/svg/Icons/32/location-blue.svg';
import ManageGroupCtrl from '../groups/ManageGroupCtrl';
import { WEEKDAYS, SALES_TYPES } from '../../../constants';

import manageLocationUrl from './manageLocation.html';

class ManageLocationCtrl extends ManageGroupCtrl {
  constructor(
    Authentication,
    Restangular,
    PersonaCollection,
    AlertService,
    ConfirmService,
    SalesService,
    IntegrationService,
    Groups,
    Utils,
    RestUtils,
    GoogleMapsService,
    UserUtils,
    $scope,
    $state,
    $location,
    $sce,
    $uibModal,
    $timeout,
    focus,
    uppercaseFirstFilter,
    displayNumberFilter
  ) {
    'ngInject';

    super(
      ConfirmService,
      Utils,
      Groups,
      PersonaCollection,
      Restangular,
      AlertService,
      UserUtils,
      $timeout,
      $state,
      $location,
      $sce,
      $uibModal,
      focus,
      uppercaseFirstFilter
    );

    Object.assign(this, {
      Authentication,
      Restangular,
      AlertService,
      ConfirmService,
      SalesService,
      IntegrationService,
      RestUtils,
      GoogleMapsService,
      $scope,
      $state,
      displayNumberFilter,
    });
  }

  $onInit() {
    this.numberWithDecimalPattern = this.Utils.getNumberValidationPattern();
    if (this.Authentication.user.wouldHaveAccessToLaborCost) {
      this.salesLoading = true;
      this._initSales();

      if (this.group.id) {
        this.tabs.unshift({ value: 'budget', label: 'budget' });
      }
    }
    super.$onInit();
    this._initGoogleMaps();

    this.icon = locationBlueSvg;

    this.timezones = this.RestUtils.getDefaultTimezones(this.group);

    this._geocodePrebound = this._geocode.bind(this);

    if (
      !this.location.id &&
      !this.Authentication.user.isAdmin &&
      this.Authentication.user.isManager
    ) {
      this.group.members.push(this.Authentication.user);
    }

    if (
      !this.location.id &&
      !this.Authentication.user.isAdmin &&
      !this.Authentication.user.orgSettings.managersCanSeeSchedulesInOtherLocations
    ) {
      this.nonDeletableMembers = [this.Authentication.user.id];
    }

    this.$scope.$watch('ctrl.group.coordinates', async (val) => this._watchCoords(val));
    this._getRemoteIpAddress();
    this.setupNameHandling();
  }

  async setupNameHandling() {
    this.canEditName = true;

    if (!this.location.origin) {
      return;
    }

    const integrations = await this.IntegrationService.getAvailableIntegrations();
    this.canEditName = !integrations
      .find((integration) => integration.type === this.location.origin)
      .userSharedFields.includes('locations');

    if (!this.canEditName) {
      this.nameTooltipText = `Edit this field on ${this.location.origin} side first for Sling to reflect any required updates.`;
    }
  }

  cancel() {
    this.sales = angular.copy(this.originalSales);
    super.cancel();
  }

  set coordinates(value) {
    this.group.coordinates = value;
    this.form.$setDirty();
  }

  get coordinates() {
    return this.group.coordinates;
  }

  get location() {
    return this.group;
  }

  set location(val) {
    this.group = val;
  }

  async loadTimezones() {
    this.timezones = await this.RestUtils.getTimezones();
  }

  async _geocode() {
    const query = this.group.address;
    if (query && query.length >= 2) {
      this.addressLoading = true;

      try {
        const matches = await this.Restangular.all('geocode/address')
          .withHttpConfig({ ignoreLoadingBar: true })
          .getList({ query });
        this.addressOptions = matches;

        if (matches.length) {
          this.addressChosen(matches[0]);
        }
      } finally {
        this.addressLoading = false;
      }
    } else {
      this.group.mapCenter = '';
      this.group.mapZoom = 1;
      this.group.coordinates = null;

      this.mapBounds = null;
    }
  }

  async _watchCoords(val) {
    if (!val) {
      return;
    }

    this.timezoneLoading = true;

    try {
      const { timeZoneId: timezone } = await this.Restangular.one('geocode/timezone').get({
        location: val.slice(1, -1).replace(/\s/g, ''),
      });
      if (timezone) {
        this.group.timezone = timezone;

        const found = this.timezones.find((tz) => tz.zone === timezone);
        if (!found) {
          this.timezones.splice(-1, 0, {
            zone: timezone,
            displayname: timezone,
          });
        }
      }
    } finally {
      this.timezoneLoading = false;
    }
  }

  addressChanged() {
    this.addressOptions = [];
    this.Utils.debounce(this._geocodePrebound, 1000);
  }

  addressChosen(address, replaceSearch) {
    this.group.mapCenter = address.coordinates;
    this.group.coordinates = address.coordinates;

    this.mapBounds = address.bounds;

    if (replaceSearch) {
      this.group.address = address.location;
    }
  }

  async _getRemoteIpAddress() {
    const { data } = await this.Authentication.sendRequest({ path: 'ip' });
    this.currentRemoteIp = data.ip;
  }

  isCurrentRemoteIpAdded() {
    return (
      this.currentRemoteIp &&
      Boolean(this.group.ipAddresses.find((addr) => addr === this.currentRemoteIp))
    );
  }

  addIpAddress(index, newIp = '') {
    let newIndex;
    if (typeof index === 'number' && index >= 0) {
      this.group.ipAddresses.splice(index + 1, 0, newIp);
      newIndex = index + 1;
    } else {
      this.group.ipAddresses.push(newIp);
      newIndex = this.group.ipAddresses.length - 1;
    }

    this.$scope.$evalAsync(() => {
      this.focus(`ip-${newIndex}`);
    });
    this.form.$setDirty();
  }

  removeIpAddress(index) {
    if (index >= 0) {
      this.group.ipAddresses.splice(index, 1);
      this.form.$setDirty();
    }
  }

  async submit() {
    this.error = false;
    if (this.form.$invalid) {
      this.form.$setSubmitted();
      return;
    }

    if (
      !this.Authentication.user.isAdmin &&
      !this.location.members.find((m) => m.id === this.Authentication.user.id)
    ) {
      await this.ConfirmService.ask(
        'Are you sure you want to remove yourself from this location? You will not be able to edit this location anymore and you will not be able to add yourself back.'
      );
    }

    const shouldSaveSales = this.budgetForm?.$dirty;
    let result;
    this.group.ipAddresses = this.group.ipAddresses.filter((a) => a); // filter out empty

    if (!this.group.id) {
      result = await this.baseSubmit();

      if (this.sales) {
        this.sales.map((sale) => (sale.locationId = result.id));
      }
    }

    if (shouldSaveSales) {
      await this.SalesService.saveDefaultSales(this.sales);
      this._initSales();
    }
    return result || (await this.baseSubmit());
  }

  _formatSalesAmount(sale) {
    return {
      ...sale,
      amount: sale.amount ? this.displayNumberFilter(sale.amount) : sale.amount,
    };
  }

  async _initSales() {
    this.sales = [];
    this.weekdays = this._getWeekdays();
    const defaultSales = (await this.SalesService.getDefaultSales(this.group.id)) || [];
    this.weekdays.forEach(({ value }) => {
      const index = defaultSales.findIndex((sale) => sale.weekday === value);
      if (index < 0) {
        this.sales[value] = this.SalesService.getEmptyProjectedSale(
          SALES_TYPES.default,
          this.group.id,
          value
        );
      } else if (defaultSales[index].amount) {
        const [number, fraction] = defaultSales[index].amount.split('.');
        this.sales[value] =
          fraction === '00'
            ? { ...defaultSales[index], amount: number }
            : { ...defaultSales[index] };
      } else {
        this.sales[value] = { ...defaultSales[index] };
      }
    });

    this.originalSales = angular.copy(this.sales);
    this.salesLoading = false;
  }

  _getWeekdays() {
    // server side implementation is 0 based, starting with Monday
    return [...WEEKDAYS].map((item) => ({
      ...item,
      value: item.value > 0 ? item.value - 1 : 6,
    }));
  }

  get totalSales() {
    return this.displayNumberFilter(this.SalesService.sumSales(this.sales));
  }

  /*
    analogically to ManagePositionCtrl - workaround for using super inside async method
   */
  baseSubmit() {
    return super.submit();
  }

  async _initGoogleMaps() {
    this.googlemaps = this.group ? await this.GoogleMapsService.load() : null;
    if (this.googlemaps === null) {
      this.AlertService.add('Maps Error');
    }

    this.showGoogleMaps = true;
  }

  selectCountryCode({ value, code }) {
    this.group.countryCode = value;
    this.group.countryISOCode = code;
  }
}

export default {
  controller: ManageLocationCtrl,
  controllerAs: 'ctrl',
  templateUrl: manageLocationUrl,
  bindings: {
    group: '<',
    previousState: '<',
    tab: '@',
  },
};
